import {useEffect, useMemo, useState} from 'react';
import {Grid} from '@mui/material';
import {useNavigate} from 'react-router';
import DataTable from "../../../component/DataTable/DataTable";
import {INGESTION_SITES} from "../../../utils/constants";
import {ConfirmDialog, SearchBar} from "../../../utils/uiUtils";
import {deleteCustomer, fetchCustomers} from "../../../utils/apiCalls";
import {fetchCompanyList} from "../../../utils/dashboardCalls";
import {CLIENT_LIST_TABLE_COLUMNS} from "./clientListTableConfig";
import {renderClientActions, renderClientAvatar, renderContactAddress, renderPrimaryContact} from "./clientListRenderHelpers";

const workLocationLabelMap = Object.fromEntries(INGESTION_SITES.map(s => [s.key, s.label]));

const enrichRow = (row, codeMap) => {
    const code = codeMap[parseInt(row.arkbridge_customer_id, 10)];
    const orders = row.linked_orders || [];
    const users = row.linked_users || [];

    const orderFields = {};
    if (orders.length === 0) {
        orderFields['linked_job_0'] = 'No linked jobs';
    } else {
        orders.forEach((o, i) => {
            const jobId = String(o.tapeark_job_id).padStart(3, '0');
            orderFields[`linked_job_${i}`] = code ? `${o.order_number} (${code}-${jobId})` : o.order_number;
        });
    }

    const userFields = {};
    if (users.length === 0) {
        userFields['linked_user_0'] = 'No linked users';
    } else {
        users.forEach((u, i) => {
            userFields[`linked_user_${i}`] = `${u.first_name} ${u.last_name}`.trim();
        });
    }

    return {
        ...row,
        item_id: row.uuid,
        work_location_label: workLocationLabelMap[row.work_location] || row.work_location,
        order_count: orders.length,
        user_count: users.length,
        ...orderFields,
        ...userFields,
    };
};

export const ClientList = () => {
    const navigate = useNavigate();

    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [customerList, setCustomerList] = useState([]);
    const [allCustomerList, setAllCustomerList] = useState([]);
    const [customerCount, setCustomerCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [companyCodeMap, setCompanyCodeMap] = useState({});
    const [deleteTargetRow, setDeleteTargetRow] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const handleEditCustomer = (row) => {
        navigate('/AccountCreation', {
            state: {
                isEditClient: true,
                customerId: row.uuid,
                customerName: row.company_name,
            }
        });
    };

    const handleCreateJob = (row) => {
        navigate('/JobCreation', {
            state: {
                uuid: row.uuid,
                step: 1,
                iscomingFrom: 'customerSelection',
            }
        });
    };

    const handleDeleteCustomer = (row) => {
        setDeleteTargetRow(row);
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = () => {
        deleteCustomer(deleteTargetRow.uuid)
            .then(() => {
                setConfirmDeleteOpen(false);
                setDeleteTargetRow(null);
                getCustomers();
            })
            .catch(error => console.log(error));
    };

    // Build tooltip entries based on max counts in current page — recomputed only when customerList changes
    const columnsConfig = useMemo(() => {
        const maxOrders = Math.max(0, ...customerList.map(r => (r.linked_orders || []).length));
        const maxUsers = Math.max(0, ...customerList.map(r => (r.linked_users || []).length));

        const linkedJobsColumn = {
            name: "Linked Jobs",
            mnemonic: "order_count",
            ui_class: "int",
            shrink: true,
            header_align: "center",
            content_align: "center",
            tooltip: Array.from({length: Math.max(1, maxOrders)}, (_, i) => ({mnemonic: `linked_job_${i}`, ui_class: "str"})),
        };

        const linkedUsersColumn = {
            name: "Linked Users",
            mnemonic: "user_count",
            ui_class: "int",
            shrink: true,
            header_align: "center",
            content_align: "center",
            tooltip: Array.from({length: Math.max(1, maxUsers)}, (_, i) => ({mnemonic: `linked_user_${i}`, ui_class: "str"})),
        };

        return [
            ...CLIENT_LIST_TABLE_COLUMNS.slice(0, -1).map(col => {
                if (col.mnemonic === "company_logo_url") return {...col, custom_render: renderClientAvatar};
                if (col.mnemonic === "linked_users")    return {...col, custom_render: renderPrimaryContact};
                if (col.mnemonic === "contact_address") return {...col, custom_render: renderContactAddress};
                return col;
            }),
            linkedJobsColumn,
            linkedUsersColumn,
            {
                ...CLIENT_LIST_TABLE_COLUMNS[CLIENT_LIST_TABLE_COLUMNS.length - 1],
                custom_render: (row) => renderClientActions(row, {
                    handleEdit: handleEditCustomer,
                    handleCreateJob,
                    handleDelete: handleDeleteCustomer,
                }),
            },
        ];
    }, [customerList, handleEditCustomer, handleCreateJob, handleDeleteCustomer]);

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        setCustomerList(allCustomerList.slice((page - 1) * 10, page * 10).map(row => enrichRow(row, companyCodeMap)));
    };

    useEffect(() => {
        getCustomers();
    }, [searchQuery]);

    const getCustomers = () => {
        let allCustomers;
        fetchCustomers(searchQuery)
            .then(all => {
                allCustomers = all;
                setAllCustomerList(all);
                setCustomerCount(all.length);
                setTotalPages(Math.ceil(all.length / 10));
                return fetchCompanyList();
            })
            .then(companyData => {
                const codeMap = {};
                if (companyData?.result?.data) {
                    companyData.result.data.forEach(c => { codeMap[c.company_id] = c.code; });
                }
                setCompanyCodeMap(codeMap);
                setCustomerList(allCustomers.slice(0, 10).map(row => enrichRow(row, codeMap)));
            })
            .catch(error => console.log(error));
    };

    return (
        <>
            <Grid container spacing={0}>
                {/* Filter bar */}
                <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={2} sx={{mt: 2, py: 2}}>
                        <Grid item xs={4}>
                            <SearchBar
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onClear={() => setSearchQuery('')}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Table */}
                <Grid item xs={12}>
                    <DataTable
                        data={customerList}
                        columnsConfig={columnsConfig}
                        dataContainsFormatting={false}
                        searchQuery={searchQuery}
                        showPagination={true}
                        entriesCount={customerCount}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePageChangeCallback={handlePageChange}
                    />
                </Grid>
            </Grid>
            <ConfirmDialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Customer"
                message={`Are you sure you want to delete ${deleteTargetRow?.company_name}? This action cannot be undone.`}
            />
        </>
    );
};
